/**
 * StarAssist — universal AI bubble.
 *
 * Star-shaped launcher (same star geometry as the HTP Sports Hub logo) in the
 * bottom-right of every page. Displays the official logo image clipped inside
 * a bold star silhouette so the bot is instantly recognizable as brand.
 *
 * Extras:
 *   · Auto-shown attraction bubble ("Hi! Ask me anything…") the first time a
 *     visitor lands on the site — dismissible, remembered via localStorage.
 *   · Clear Close (X) + "End Chat" button in the panel header so users are
 *     never stuck with a modal blocking the UI.
 *   · Talks to POST /api/agent/ask. For logged-in members, it forwards their
 *     user_id + phone so the backend can unlock live-data intents.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useHubMember } from "@/lib/HubMemberContext";
import { useAuth } from "@/context/AuthContext";
import { Send, X, MessageCircle, ExternalLink, MapPin, Phone, Mail } from "lucide-react";

const HUB_WHATSAPP_FALLBACK = "919876500000";
const LOGO_SRC = "https://customer-assets-0z36b82j.emergentagent.net/job_court-booking-hub-22/artifacts/fsvri6nu_WhatsApp%20Image%202026-08-04%20at%2018.37.01.jpeg";
const TIP_KEY = "htp_assist_tip_dismissed_v1";

// Persist the same session id across page loads so conversations feel continuous
function sessionId() {
  let s = sessionStorage.getItem("star_assist_sid");
  if (!s) { s = "sa_" + Math.random().toString(36).slice(2, 12); sessionStorage.setItem("star_assist_sid", s); }
  return s;
}

// Circular brand bubble — the HTP Sports Hub logo is already round, so we
// display it inside a gold-gradient ring instead of a star silhouette.
function StarLogoBubble({ size = 64 }) {
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className="drop-shadow-[0_8px_20px_rgba(217,164,65,0.55)]"
    >
      <defs>
        <clipPath id="sa-circle-clip">
          <circle cx="50" cy="50" r="46" />
        </clipPath>
        <linearGradient id="sa-ring-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#F5C05B" />
          <stop offset="55%" stopColor="#D9A441" />
          <stop offset="100%" stopColor="#7A5C1E" />
        </linearGradient>
      </defs>
      {/* Outer gradient ring */}
      <circle cx="50" cy="50" r="50" fill="url(#sa-ring-grad)" />
      {/* Inner dark bezel */}
      <circle cx="50" cy="50" r="47" fill="#0B1120" />
      {/* Brand logo image, clipped to a clean circle */}
      <image
        href={LOGO_SRC}
        x="4" y="4" width="92" height="92"
        preserveAspectRatio="xMidYMid slice"
        clipPath="url(#sa-circle-clip)"
      />
      {/* Sharp outer stroke */}
      <circle cx="50" cy="50" r="48" fill="none" stroke="#0B1120" strokeWidth="2" />
    </svg>
  );
}

export default function StarAssist() {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [chat, setChat] = useState([
    { from: "bot", text: "Hey! I'm HTP Assist — ask me anything about HTP Sports Hub: timings, bookings, membership plans, tournaments, hostel, dues, or your ward's batch." },
  ]);
  const [busy, setBusy] = useState(false);
  const [showTip, setShowTip] = useState(false);
  const [hubPhone, setHubPhone] = useState(HUB_WHATSAPP_FALLBACK);
  const scrollerRef = useRef(null);
  const { member } = useHubMember();
  const { user } = useAuth();

  // Fetch hub contact links (WhatsApp / social / directions) from Homepage CMS.
  // These live under Admin → Homepage & Contact → Social Media Links so the founder
  // can update them from one place — no code deploys needed.
  useEffect(() => {
    let cancelled = false;
    api.get("/homepage").then(({ data }) => {
      if (cancelled) return;
      const wa = data?.contact?.socials?.whatsapp || "";
      // Accept either a full "https://wa.me/…" link or a bare phone number.
      const digits = wa.replace(/[^\d]/g, "");
      if (digits) setHubPhone(digits);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Auto-show attraction tooltip after 3s on first visit
  useEffect(() => {
    const dismissed = localStorage.getItem(TIP_KEY);
    if (dismissed) return;
    const t = setTimeout(() => setShowTip(true), 2500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [chat, open]);

  const dismissTip = (e) => {
    e?.stopPropagation();
    setShowTip(false);
    localStorage.setItem(TIP_KEY, "1");
  };

  const launch = () => {
    setOpen(true);
    dismissTip();
  };

  const endChat = () => {
    setOpen(false);
    setChat([
      { from: "bot", text: "Hey! I'm HTP Assist — ask me anything about HTP Sports Hub: timings, bookings, membership plans, tournaments, hostel, dues, or your ward's batch." },
    ]);
    setMsg("");
  };

  const ask = async (text) => {
    if (!text.trim()) return;
    const q = text.trim();
    setChat((c) => [...c, { from: "me", text: q }]);
    setMsg(""); setBusy(true);
    try {
      const { data } = await api.post("/agent/ask", {
        message: q,
        session_id: sessionId(),
        phone: member?.phone || null,
        user_id: (user?.role === "parent" || user?.role === "member") ? user?.id : (member?.user_id || null),
      });
      setChat((c) => [...c, {
        from: "bot",
        text: data.answer,
        escalate: data.escalate,
        followups: data.followups || [],
        links: data.links || [],
      }]);
    } catch (e) {
      setChat((c) => [...c, { from: "bot", text: "Hmm, something went wrong. Please try again — or tap WhatsApp to reach us directly.", escalate: true }]);
    } finally {
      setBusy(false);
    }
  };

  const openWhatsApp = (contextText) => {
    const prefill = encodeURIComponent(
      contextText || `Hi HTP Sports Hub, I was chatting with HTP Assist and need human help.\n\nMy question: ${chat.filter((c) => c.from === "me").pop()?.text || ""}`,
    );
    window.open(`https://wa.me/${hubPhone}?text=${prefill}`, "_blank", "noopener");
  };

  return (
    <>
      {/* Bubble launcher — star-shaped, hosts the real brand logo */}
      {!open && (
        <div className="fixed bottom-5 right-5 z-50 flex items-end gap-3">
          {/* Attraction speech bubble */}
          {showTip && (
            <div
              data-testid="star-assist-tip"
              className="relative mb-2 max-w-[240px] bg-white text-black shadow-xl border-2 border-brand-orange px-3 py-2 rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl animate-[fadeIn_0.4s_ease-out]"
            >
              <button
                data-testid="star-assist-tip-close"
                onClick={dismissTip}
                aria-label="Dismiss"
                className="absolute -top-2 -right-2 w-5 h-5 bg-black text-white rounded-full flex items-center justify-center hover:bg-brand-orange"
              >
                <X size={11} />
              </button>
              <div className="font-display font-black text-sm leading-tight">
                Hi there! <span className="text-brand-orange">Need help?</span>
              </div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-black/60 mt-1">
                Ask me anything about the Hub
              </div>
              {/* Tail pointing down-right toward the star */}
              <div className="absolute -bottom-[9px] right-4 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[10px] border-t-white" />
            </div>
          )}

          <button
            onClick={launch}
            data-testid="star-assist-launcher"
            aria-label="Ask HTP Assist"
            className="relative group animate-[pulse_2.6s_ease-in-out_infinite] hover:scale-110 transition-transform"
          >
            <StarLogoBubble size={68} />
            {!showTip && (
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-brand-orange border-2 border-black rounded-full animate-ping" />
            )}
          </button>
        </div>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="star-assist-panel"
          className="fixed bottom-4 right-4 z-50 w-[92vw] max-w-sm h-[600px] max-h-[85vh] flex flex-col bg-[#0B1120] border-2 border-brand-orange shadow-2xl shadow-brand-orange/30"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-white/10 bg-gradient-to-r from-brand-orange to-amber-400 text-black">
            <div className="flex items-center gap-2">
              <StarLogoBubble size={40} />
              <div>
                <div className="font-display font-black text-lg leading-none">HTP Assist</div>
                <div className="font-mono text-[10px] uppercase tracking-widest opacity-70">In-house AI · HTP Sports Hub</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="star-assist-end"
                onClick={endChat}
                title="End chat"
                className="hidden sm:inline-flex items-center gap-1 bg-black/80 hover:bg-black text-white px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest"
              >
                End Chat
              </button>
              <button
                data-testid="star-assist-close"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
                className="w-8 h-8 flex items-center justify-center bg-black/80 hover:bg-black text-white"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Chat body */}
          <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {chat.map((c, i) => (
              <div key={i} className={`max-w-[85%] ${c.from === "me" ? "ml-auto" : ""}`}>
                <div className={c.from === "me"
                  ? "bg-brand-blue text-white px-3 py-2 text-sm rounded-tl-xl rounded-tr-xl rounded-bl-xl"
                  : "bg-white/6 text-white px-3 py-2 text-sm rounded-tl-xl rounded-tr-xl rounded-br-xl whitespace-pre-line"}>
                  {c.text}
                </div>
                {/* Action buttons — social links, maps, phone, email */}
                {(c.links || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {c.links.map((lk, k) => {
                      const Icon = lk.kind === "whatsapp" ? MessageCircle
                        : lk.kind === "maps" ? MapPin
                        : lk.kind === "phone" ? Phone
                        : lk.kind === "email" ? Mail
                        : ExternalLink;
                      const color = lk.kind === "whatsapp"
                        ? "bg-green-600 hover:bg-green-500 text-white"
                        : lk.kind === "phone"
                        ? "bg-brand-blue hover:bg-blue-500 text-white"
                        : "bg-brand-orange hover:bg-amber-400 text-black";
                      const isTel = lk.url.startsWith("tel:") || lk.url.startsWith("mailto:");
                      return (
                        <a
                          key={k}
                          href={lk.url}
                          target={isTel ? undefined : "_blank"}
                          rel={isTel ? undefined : "noopener noreferrer"}
                          data-testid={`star-assist-link-${lk.kind}`}
                          className={`${color} inline-flex items-center gap-2 px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest`}
                        >
                          <Icon size={12} /> {lk.label}
                        </a>
                      );
                    })}
                  </div>
                )}
                {c.escalate && (c.links || []).length === 0 && (
                  <button
                    onClick={() => openWhatsApp()}
                    data-testid="star-assist-whatsapp"
                    className="mt-2 inline-flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest"
                  >
                    <MessageCircle size={12} /> Open WhatsApp
                  </button>
                )}
                {(c.followups || []).map((f, j) => (
                  <button
                    key={j} onClick={() => ask(f)}
                    className="mt-2 mr-2 inline-block border border-brand-orange/50 text-brand-orange hover:bg-brand-orange hover:text-black px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest"
                  >
                    {f}
                  </button>
                ))}
              </div>
            ))}
            {busy && <div className="text-white/40 text-xs">HTP Assist is thinking…</div>}
          </div>

          {/* Composer */}
          <form
            onSubmit={(e) => { e.preventDefault(); ask(msg); }}
            className="border-t border-white/10 p-3 flex gap-2 bg-black/40"
          >
            <input
              value={msg} onChange={(e) => setMsg(e.target.value)}
              data-testid="star-assist-input"
              placeholder="Ask about dues, bookings, timings…"
              className="flex-1 bg-black/60 border border-white/15 px-3 py-2 text-sm outline-none focus:border-brand-orange"
            />
            <button type="submit" data-testid="star-assist-send" disabled={busy || !msg.trim()}
              className="bg-brand-orange text-black px-3 py-2 font-bold hover:bg-white disabled:opacity-40">
              <Send size={14} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
